import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { Typography, Container, Button, Box } from "@mui/material";
import api from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import { useTranslation } from "react-i18next";
import LoaderContainer from "../../components/LoaderContainer";
import LabTestIcon from "../../assets/Icon/LabTestIcon.png";
import { useAlert } from "../../components/Alert";
import SoilTestScoreCard from "./SoilTestScoreCard";
import FertilizerRecommendationCard from "./FertilizerRecommendationCard";
import SimpleHeader from "../../components/SimpleHeader";
import html2pdf from "html2pdf.js";
import SoilTestReportCard from "../../components/Cards/SoilTestReportCard";

const ExternalSoilTestReport = () => {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [scores, setScores] = useState(null);
  const [recFertilizer, setRecFertilizer] = useState([]);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [isRecLoading, setIsRecLoading] = useState(true);
  const { showAlert } = useAlert();
  const [isPrint, setIsPrint] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    if (reportId) {
      loadReport(reportId);
      loadFertilizerRecommendations(reportId);
      loadHealthScores(reportId);
    }
  }, [reportId]);

  const loadHealthScores = (reportId) => {
    api
      .get(`/soil-testing/get-soil-report-scores/${reportId}`)
      .then((result) => {
        if (result && result.data && result.data.scores) {
          const { scores } = result.data;
          setScores(scores);
        } else {
          showAlert("error", "Failed to load soil health scores");
          setReport(null);
        }
      })
      .catch((err) => {
        console.error("Error: ", err);
        showAlert("error", "Failed to load soil health scores");
      });
  };

  const loadReport = (report_id) => {
    api
      .get(`/soil-testing/test-report/${report_id}`)
      .then((result) => {
        if (result && result.data) {
          setReport(result.data);
        } else {
          showAlert("error", "Failed to load Soil Test Report");
          setReport(null);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error: ", err);
        showAlert("error", "Failed to load Soil Test Reports");
        setLoading(false);
      });
  };

  const loadFertilizerRecommendations = (report_id) => {
    api
      .get(
        `/soil-testing/get-soil-fertilizer-recommendations/?report_id=${report_id}`
      )
      .then((result) => {
        if (result && result.data) {
          setRecFertilizer(result.data.recommendations || []);
        } else {
          showAlert("error", "Failed to load soil recommendations");
          setReport(null);
        }
        setIsRecLoading(false);
      })
      .catch((err) => {
        console.error("Error: ", err);
        showAlert("error", "Failed to load soil recommendations");
        setIsRecLoading(false);
      });
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    title: `Soil Test Report - ${report?.id}`,
    print: async (printIframe) => {
      const document = printIframe.contentDocument;
      if (document) {
        const html = document.getElementsByTagName("html")[0];
        console.log(html);
        await html2pdf()
          .from(html)
          .set({
            filename: `SoilTestReport-${report?.id}.pdf`,
          })
          .save();
      }
    },
    onAfterPrint: () => {
      setIsPrint(false);
    },
  });

  return (
    <>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container
          maxWidth="xs"
          style={{
            height: report ? undefined : "calc(100vh - 9rem)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          {report ? (
            <>
              <Button
                sx={{
                  marginTop: "10px",
                }}
                variant="outlined"
                fullWidth
                onClick={() => {
                  setIsPrint(true);
                  setTimeout(() => {
                    handlePrint();
                  }, 500);
                }}
              >
                {t("Download Report")}
              </Button>
              <Box
                style={{
                  padding: "12px",
                  // margin: "auto",
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
                ref={printRef}
                fullWidth
              >
                {isPrint && <SimpleHeader />}
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontSize: "20px",
                    fontWeight: 700,
                    marginTop: "10px",
                  }}
                >
                  {t("Soil Test Report")}
                </Typography>
                {report && (
                  <SoilTestReportCard
                    data={{
                      [t("Soil Test ID")]: report.id,
                      [t("Farm ID")]: report.farm_land,
                      [t("Farmer Name")]:
                        report.farmer_details?.farmerName || "NA",
                      [t("Crop Name")]: report.farmer_details?.cropName || "NA",
                      [t("Farm Area")]: report.farmer_details?.farmArea || "NA",
                      [t("Nitrogen")]: report.nitrogen,
                      [t("Phosphorus")]: report.phosphorus,
                      [t("Potassium")]: report.potassium,
                      [t("pH")]: report.ph,
                      [t("Organic Carbon")]: report.organic_carbon,
                      [t("Humidity")]: report.humidity,
                      [t("temperature")]: report.temperature,
                      [t("Electric Conductivity")]:
                        report.electric_conductivity,
                      [t("Report Date")]: (report.report_time || "").split(
                        "T"
                      )[0],
                    }}
                  />
                )}
                {scores && (
                  <SoilTestScoreCard
                    scores={scores}
                    title={t("Soil Test Scores")}
                    showHealthScore={false}
                  />
                )}
                {isPrint && (
                  <div
                    style={{
                      height: "32px",
                    }}
                  />
                )}
                {!isRecLoading && (
                  <FertilizerRecommendationCard
                    recommendations={recFertilizer}
                  />
                )}
              </Box>
            </>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <div style={{ marginBottom: "30px" }}>
                <img
                  src={LabTestIcon}
                  alt="My Crops Icon"
                  style={{
                    width: "127px",
                    height: "127px",
                    marginBottom: "10px",
                  }}
                />
              </div>
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, textAlign: "center" }}
              >
                {t("No Soil Test Report Found for this ID")}
              </Typography>
            </div>
          )}
        </Container>
      </LoaderContainer>
    </>
  );
};

export default ExternalSoilTestReport;
